import { storyblokEditable } from '@storyblok/react/rsc';
import { resolveAsset, optimizeImage, getImageDimensions, srcSet, styleOverrides } from '@/lib/helpers';

const Image = ({ blok }) => {
  const src = resolveAsset(blok.src);
  if (!src) return null;

  const maxWidth = blok.max_width || '';
  // A fixed size (e.g. size-[100px]) pins BOTH dimensions, so the layout box is
  // known before the file loads — see the lazy-loading note below.
  const fixedSize = blok.fixed_size || '';
  const dims = getImageDimensions(src);
  // sizes hint: full-width fills the viewport; a capped image its cap; otherwise
  // it renders at its natural width.
  const cap = maxWidth.match(/\[(\d+)px\]/) || fixedSize.match(/\[(\d+)px\]/);
  const sizes = cap
    ? `(max-width: ${cap[1]}px) 100vw, ${cap[1]}px`
    : blok.width_full || !dims?.width ? '100vw' : `${dims.width}px`;

  // Editor toggles: fill width / fill height (width/height: 100%) and/or crop to
  // fill (object-fit: cover). By default the image renders at its natural size.
  // A fixed size without crop fits the whole image inside the square instead of
  // stretching it, so a wide logo shows in full rather than squashed.
  const className = [
    fixedSize,
    blok.width_full && !fixedSize ? 'w-full' : '',
    blok.height_full ? 'h-full' : fixedSize ? '' : 'h-auto',
    blok.image_cover ? 'object-cover' : fixedSize ? 'object-contain' : '',
    maxWidth,
    blok.border_radius || '',
  ].filter(Boolean).join(' ');

  // A fixed size gives the same guarantee the intrinsic dimensions would: a
  // laid-out, non-zero box. That's all the lazy-loading and srcset paths below
  // actually need, so treat either as "size known".
  const sizeKnown = Boolean(dims || fixedSize);

  // Assets uploaded through the Management API can come back without the
  // /WIDTHxHEIGHT/ segment that getImageDimensions() reads, so dims is null and
  // no width/height attributes are emitted. That used to deadlock: with no
  // intrinsic size the layout box is 0x0, a zero-area element never satisfies
  // the lazy-loading intersection heuristic, so the image never loaded — and so
  // never got a size. It has to load before it can be measured, so for these we
  // skip lazy loading and the responsive srcset (whose `sizes` presumes a laid-out
  // box) and let the browser use the file's own intrinsic size. Assets that do
  // carry dimensions keep the optimised responsive path.
  return (
    <img
      {...storyblokEditable(blok)}
      src={optimizeImage(src, dims?.width || 0, 0)}
      srcSet={sizeKnown ? srcSet(src) : undefined}
      sizes={sizeKnown ? sizes : undefined}
      alt={blok.alt || blok.src?.alt || ''}
      width={dims?.width || undefined}
      height={dims?.height || undefined}
      className={className}
      style={styleOverrides(blok)}
      loading={sizeKnown ? 'lazy' : 'eager'}
    />
  );
};

export default Image;
